import React, {Component} from "react";
import {connect} from "react-redux";
import URLSearchParams from 'url-search-params'
import {Redirect, Route, Switch} from "react-router-dom";
import {ConfigProvider} from "antd";
import {IntlProvider} from "react-intl";

import moment from "moment";
import AppLocale from "lngProvider";
import MainApp from "./MainApp";
import {fetchAccountCurrent} from "../../appRedux/actions";
import {onLayoutTypeChange, onNavStyleChange, setThemeType} from "appRedux/actions/Setting";

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK
} from "../../constants/ThemeSetting";
import {initializeWebSocketsChannel} from "../../appRedux/actions";
import { isShoperApp } from "../../util/appType";
import { IntegrationType } from "../../util/integrationType";
import { getDesignTheme } from "../../util/designTheme";
import { resolveStartPage } from "../../util/userPreferences";
import { applyThemeModeToDocument, getStoredStartPage, getThemeMode, UI_PREFERENCES_EVENT, watchSystemTheme } from "../../util/uiPreferences";

class App extends Component {
  state = {
    themeMode: isShoperApp() ? 'light' : getThemeMode(),
  };

  onUiPreferencesChange = () => {
    const themeMode = isShoperApp() ? 'light' : getThemeMode();
    applyThemeModeToDocument(themeMode);
    document.documentElement.classList.toggle('shoper-app', isShoperApp());
    if (themeMode !== this.state.themeMode) {
      this.setState({themeMode});
    }
  };

  componentDidMount() {
    applyThemeModeToDocument(this.state.themeMode);
    document.documentElement.classList.toggle('shoper-app', isShoperApp());
    window.addEventListener(UI_PREFERENCES_EVENT, this.onUiPreferencesChange);
    // With the theme on "Auto", follow the operating system when it switches.
    this.unwatchSystemTheme = watchSystemTheme(this.onUiPreferencesChange);
  }

  componentWillUnmount() {
    window.removeEventListener(UI_PREFERENCES_EVENT, this.onUiPreferencesChange);
    if (this.unwatchSystemTheme) this.unwatchSystemTheme();
  }

  setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  setNavStyle = (navStyle) => {
    if (navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  componentWillMount() {
    setTimeout(() => {
      this.props.fetchAccountCurrent();
    }, 0);

    const params = new URLSearchParams(this.props.location.search);

    if (params.has("theme")) {
      this.props.setThemeType(params.get('theme'));
    }
    if (params.has("nav-style")) {
      this.props.onNavStyleChange(params.get('nav-style'));
    }
    if (params.has("layout-type")) {
      this.props.onLayoutTypeChange(params.get('layout-type'));
    }
  }

  render() {
    const {match, location, themeType, layoutType, navStyle, locale} = this.props;
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add('dark-theme');
    }

    if (
        !['/aws-marketplace/setup-form'].includes(location.pathname)
        && this.props.accounts?.isLoadedPhoneNumbers
        && ['SETUP_FORM', 'VERIFICATION'].includes(this.props.accounts.status)
        && this.props.accounts.integrations?.find(integration => integration.type === IntegrationType.aws_marketplace)
    ) {
        return <Redirect to={'/aws-marketplace/setup-form'}/>;
    } else if (
        !['/no-phone-number', '/channels/choose-account', '/aws-marketplace/setup-form'].includes(location.pathname)
        && this.props.accounts?.isLoadedPhoneNumbers
        && !this.props.accounts.phoneNumbersAll.length
    ) {
        return <Redirect to={'/no-phone-number'}/>;
    } else if (location.pathname === '/') {
      // The user's start page (cached in this browser); without one yet, the dashboard, and the
      // preferences loader moves them on if their account has a start page saved.
      // In the Shoper app (no start page choice) it is always the dashboard.
      const stored = isShoperApp() ? null : getStoredStartPage();
      const startPage = stored ? resolveStartPage(stored) : null;
      if (!stored && !isShoperApp()) {
        try {
          window.sessionStorage.setItem('2way.rootRedirect', '1');
        } catch (e) {
          // ignore
        }
      }
      return <Redirect to={startPage || '/my-dashboard' + location.search}/>;
    }

    this.setLayoutType(layoutType);

    this.setNavStyle(navStyle);

    // Shoper Connect look (shoper.less); the Shoper sign-in sets the provider after start.
    document.documentElement.classList.toggle('shoper-app', isShoperApp());

    const currentAppLocale = AppLocale[locale.locale] || AppLocale.en;
    // Dates formatted with moment follow the interface language.
    if (moment.locale() !== locale.locale) moment.locale(AppLocale[locale.locale] ? locale.locale : 'en');
    return (
      <ConfigProvider
          locale={currentAppLocale.antd}
          theme={getDesignTheme(this.state.themeMode, isShoperApp())}
      >
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}>
          <Switch>
            <Route path={`${match.url}`} component={MainApp} />
          </Switch>
        </IntlProvider>
      </ConfigProvider>
    )
  }
}

const mapStateToProps = (state) => {
  const {locale, navStyle, themeType, layoutType} = state.settings;
  return {locale, navStyle, themeType, layoutType, accounts: state.accounts}
};
export default connect(mapStateToProps, {setThemeType, onNavStyleChange, onLayoutTypeChange, fetchAccountCurrent, initializeWebSocketsChannel})(App);
